import { useEffect, useRef } from 'react';

// A quick flick has to travel this far, in css pixels, to count.
export const SWIPE_MIN_DISTANCE = 50;
// A flick slower than this is someone dragging; it counts only past SWIPE_COMMIT_FRACTION.
export const SWIPE_MAX_DURATION = 800;
// A slow drag counts once it has crossed this much of the area's width.
export const SWIPE_COMMIT_FRACTION = 0.35;
// Movement under this is a tap that wobbled, not yet a direction.
export const SWIPE_SLOP = 10;

/**
 * Where a finger's travel steps to: 1 for the next (swiped left), -1 for the previous (swiped
 * right), 0 for neither. A quick flick needs SWIPE_MIN_DISTANCE; a slower drag has to cross
 * SWIPE_COMMIT_FRACTION of `width`, the way a finger pulls the next page over. Either way it has to
 * be mostly sideways, or it is a vertical scroll that drifted.
 */
export function swipeDirection(dx, dy, duration, width = 0) {
	if(Math.abs(dx) < Math.abs(dy) * 2) {
		return 0;
	}
	const flicked = duration <= SWIPE_MAX_DURATION && Math.abs(dx) >= SWIPE_MIN_DISTANCE;
	const pulled = width > 0 && Math.abs(dx) >= width * SWIPE_COMMIT_FRACTION;
	if(!flicked && !pulled) {
		return 0;
	}
	return dx < 0 ? 1 : -1;
}

/**
 * Whether a touch began on the area itself. React bubbles a portal's events through the component
 * tree, so a popup the area renders reaches its handlers from outside its DOM; and a dialog drawn
 * inside it is not part of what swipes either.
 */
function startsInArea(e) {
	const target = e.target;
	if(!target || !e.currentTarget.contains(target)) {
		return false;
	}
	return !(target.closest && target.closest('[role="dialog"], .modal'));
}

/**
 * Pointer handlers for an area a touch can swipe sideways. `onSwipe(direction)` is called when one
 * lands, and `onDrag(dx)` follows the finger on the way, once it has clearly gone sideways, so the
 * area can move with it. `onDrag(0)` says the gesture is over; it comes before `onSwipe`.
 *
 * A mouse never swipes: on a desktop a sideways mouse movement is something being dragged.
 *
 * `disabled` is a react-beautiful-dnd drag, which on a touch screen starts after a short hold. A
 * gesture during which it was ever true is not a swipe, even when the drop has already cleared it by
 * the time the finger lifts.
 *
 * Only a touch that starts on the area's own DOM, outside any dialog, counts (startsInArea).
 *
 * A gesture that goes vertical first is a scroll, and one the browser takes over for scrolling is
 * cancelled; neither is a swipe. The click a lifted finger can still produce is swallowed after a
 * swipe, so whatever is under it does not open.
 */
export function useSwipe({ onSwipe, onDrag = null, disabled = false }) {
	const gesture = useRef(null);
	const swallowClick = useRef(false);

	const release = () => {
		const start = gesture.current;
		gesture.current = null;
		if(start && start.sideways && onDrag) {
			onDrag(0);
		}
		return start;
	};

	if(disabled && gesture.current) {
		gesture.current.void = true;
	}
	useEffect(() => {
		if(disabled) {
			release();
		}
	}, [disabled]);

	return {
		onPointerDown(e) {
			swallowClick.current = false;
			if(!onSwipe || e.pointerType === 'mouse' || e.isPrimary === false || !startsInArea(e)) {
				gesture.current = null;
				return;
			}
			gesture.current = {
				x: e.clientX,
				y: e.clientY,
				at: Date.now(),
				width: e.currentTarget.clientWidth || 0,
				sideways: false,
				void: disabled,
			};
		},
		onPointerMove(e) {
			const start = gesture.current;
			if(!start || start.void) {
				return;
			}
			const dx = e.clientX - start.x;
			const dy = e.clientY - start.y;
			if(!start.sideways) {
				if(Math.abs(dy) >= SWIPE_SLOP && Math.abs(dy) >= Math.abs(dx)) {
					start.void = true;
					return;
				}
				if(Math.abs(dx) < SWIPE_SLOP) {
					return;
				}
				start.sideways = true;
			}
			if(onDrag) {
				onDrag(dx);
			}
		},
		onPointerUp(e) {
			const start = release();
			if(!start || start.void || disabled) {
				return;
			}
			const direction = swipeDirection(e.clientX - start.x, e.clientY - start.y, Date.now() - start.at, start.width);
			if(direction !== 0) {
				swallowClick.current = true;
				onSwipe(direction);
			}
		},
		onPointerCancel() {
			release();
		},
		onClickCapture(e) {
			if(swallowClick.current) {
				swallowClick.current = false;
				e.stopPropagation();
				e.preventDefault();
			}
		},
	};
}
